import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { centroid } from "@turf/turf";
import { LngLat, Map } from "mapbox-gl";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { GetNAFV3Contract, getContractAgent } from "../../api/api";
import Avatar from "../../components/Avatar";
import { EmptyView } from "../../components/EmptyView";
import { FirebaseImage } from "../../components/FirebaseImage";
import { Loader } from "../../components/Loader";
import MapBox from "../../components/MapBox";
import { defaultLat, defaultLng, isArrayNullOrEmpty, showErrorMessage } from "../../helper/helper";
import { addContractDetailLayers, updatePickedUps, updatePlots, zoomForCoordinates } from "../../models/MapBoxDataModel";
import { buildContractNafV3ForDetail } from "../../models/contract/Contract";
import { ContractInterface } from "../../models/contract/ContractInterface";
import { ContractNAM } from "../../models/contract/ContractNAM";
import { Harvest } from "../../models/contract/Harvest";
import { Person } from "../../models/person/Person";
import { ReactComponent as Check } from "../../rsc/check.svg";
import { colorPrimary } from "../../rsc/colors/colors";
import { ReactComponent as LogoDO } from "../../rsc/ic_do.svg";
import { ReactComponent as Money } from "../../rsc/ic_money.svg";
import { ReactComponent as Supply } from "../../rsc/ic_supply.svg";
import "./ContractDetail.css";
import { StatementComponent } from "./component/StatementComponent";

type ContractDetailProps = {
    client: ApolloClient<NormalizedCacheObject>
}

function ContractDetail(props: ContractDetailProps) {

    const [contract, setContract] = useState<ContractInterface>(undefined)
    const [center, setCenter] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const mapRef = useRef(null)
    const contractRef = useRef<ContractInterface>(null)
    const popupsRef = useRef([])
    const pickedUpLocation = useRef([])
    const [pickUpOnMap, setPickUpOnMap] = useState([])

    const [param] = useSearchParams()

    var id = param.get("id")
    var contract_type = param.get("contract_type")
    // var contractId = param.get("contract_id")

    const { t, i18n } = useTranslation()

    function isNafV3(type: string = contract_type): boolean {
        return type === "NAF_V3"
    }

    function fetchContractFromSale(saleId: number, type) {
        setIsLoading(true)
        const query = isNafV3(type) ? GetNAFV3Contract : getContractAgent
        props.client.query({
            query: query,
            variables: {
                statementId: Number(saleId),
            },
            context: {
                headers: {
                    "role": "usermill"
                }
            }
        }).then((data) => {
            console.log('contract', data)
            const contract = data.data.contract
            var contractBuilt: ContractInterface
            if (isNafV3(type)) {
                contractBuilt = buildContractNafV3ForDetail({ ...contract, contract_type: contract_type })
                setCenter(extractCenter(contractBuilt))
            } else {
                contractBuilt = ContractNAM.build({ ...contract, contract_type: contract_type })
                setCenter(extractCenter(contractBuilt))
            }
            setContract(contractBuilt)
            contractRef.current = contractBuilt
        }).catch(e => {
            showErrorMessage(e.message)
        })
            .finally(() => {
                setIsLoading(false)
            })
    }

    // function fetchSaleFromContractId(cId: number) {
    //     setIsLoading(true)
    //     const query = GetSaleFromContract
    //     props.client.query({
    //         query: query,
    //         variables: {
    //             id: cId
    //         },
    //     }).then((data) => {
    //         console.log(data)
    //         // fetchContractFromSale(data.data)
    //     }).finally(() => {
    //         setIsLoading(false)
    //     })
    // }

    function centerMap() {
        var coords: LngLat[] = []
        if (contractRef.current.getHarvest()?.length > 0) {
            coords = [...contractRef.current.getHarvest()]?.filter(h => h.land_plot).map(h => h.land_plot).flatMap(p => p.polygon ? p.polygon.coordinates[0].map(co => new LngLat(co[0], co[1])) : [])
        }
        if (pickedUpLocation.current?.length > 0) {
            coords = [...coords, ...pickedUpLocation.current.map(co => new LngLat(co[0], co[1]))]
        }
        zoomForCoordinates(mapRef.current, coords, 14, 0)
    }

    useEffect(() => {
        if (contractRef.current) {
            updatePlots(mapRef.current, contractRef.current.getHarvest())
            centerMap()
        }
    }, [contractRef.current])

    useEffect(() => {
        const changeLanguageHandler = (l) => {
            if (mapRef.current) {
                // if (contract.isAgentContract()) {
                //     addContractDetailNAMLayers(mapRef.current)
                // } else {
                //     addContractDetailNAFLayers(mapRef.current, contractRef.current.harvests.map(h => h.harvest), popupsRef.current, t)
                // }
            }
        };

        i18n.on('languageChanged', changeLanguageHandler);

        return () => {
            i18n.off('languageChanged', changeLanguageHandler);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [i18n])

    const extractCenter = (contractExtract: ContractInterface) => {
        const harvest = contractExtract.getHarvest()?.at(0)
        if (harvest) {
            let coo = harvest?.land_plot?.polygon?.coordinates
            if (coo) {
                let middle = centroid(
                    {
                        'type': 'Feature',
                        'properties': {
                        },
                        'geometry': {
                            'type': 'Polygon',
                            'coordinates': coo
                        }
                    }
                )
                return middle.geometry.coordinates
            } else {
                return null
            }
        }
    }

    const marketDay = () => {
        return contract?.getMargetDay()
    }

    function harvestsPhotos(harvest: Harvest) {
        var photos = new Set(harvest.fruitPhotos().map(p => p.uri))
        return (
            <div key={harvest.land_plot?.name}>
                {
                    photos?.size > 0 ?
                        <div className="cd-photo-section">
                            <div className="cd-photo-section-header">
                                <Supply />
                                <p>{t('contract_land_plot_header', { name: harvest.land_plot?.name })}</p>
                            </div>
                            <div className="cd-photo-grid">
                                {[...photos].map((p, i) => {
                                    return <FirebaseImage onMetadataLoaded={(metadata) => {
                                        const location = metadata.photo_location
                                        try {
                                            const jsonObject = JSON.parse(location);
                                            pickedUpLocation.current.push(jsonObject.coordinates)
                                            if (pickedUpLocation.current.length === photos.size) {
                                                setPickUpOnMap(pickedUpLocation.current)
                                                updatePickedUps(mapRef.current, pickedUpLocation.current)
                                                centerMap()
                                            }
                                        } catch (error) {
                                            console.error('Error parsing JSON:', error);
                                        }
                                    }} key={p} style={{ width: "100%", height: "100%" }} uri={p} />
                                }
                                )}
                            </div>
                        </div>
                        : <div key={harvest?.land_plot?.name}></div>
                }
            </div>
        )
    }

    function displayReceipt(contract: ContractInterface) {
        const photos = contract.receiptPhotos().map(p => p.uri)
        return (
            photos ?
                <div className="cd-photo-section">
                    <div className="cd-photo-section-header">
                        <Money />
                        <p>{t('contract_receipt_header')}</p>
                    </div>
                    <div className="cd-photo-grid">
                        {
                            photos.map(p =>
                                <FirebaseImage key={p} style={{ width: "100%", height: "100%" }} uri={p} />
                            )
                        }
                    </div>
                </div>
                : <></>
        )
    }

    function shouldDisplayHarvest() {
        return (contract?.harvestPhotos()?.length ?? 0) > 0
    }

    function shouldDisplayReceipt(): boolean {
        return !isArrayNullOrEmpty(contract?.receiptPhotos())
    }

    function generateHarvestPicture() {
        return (contract?.getHarvest()?.map(h => harvestsPhotos(h)))
    }


    function hasPositionOnMap(): boolean {
        return contract?.getHarvest()?.length > 0 || pickUpOnMap.length > 0
    }

    function onMapLoaded(map: Map) {
        mapRef.current = map
        addContractDetailLayers(mapRef.current, popupsRef.current)
        // if (contractId) {
        //     fetchSaleFromContractId(Number(contractId))
        // } else {
        fetchContractFromSale(Number(id), contract_type)
        // }
    }

    function webGlNotActivated() {
        fetchContractFromSale(Number(id), contract_type)
    }

    const supplier: Person = isNafV3(contract_type) ? contract?.getPersonTransporter() : contract?.getPersonAgent()

    return (
        <div style={{ flex: "1", paddingBottom: "20px" }}>
            <div style={{ padding: "20px 28px 0 28px" }}>
                <h1 style={{ margin: "0 0 20px 0" }}>{t('contract_detail')} {contract?.getId()}</h1>

                {/* Buyer bar with status */}
                <div className="cd-buyer-bar">
                    <div className="cd-buyer-info">
                        <LogoDO />
                        <p className="cd-buyer-name">{contract?.getBuyer()?.name}</p>
                        <p className="cd-market-day">{marketDay()}</p>
                    </div>
                    <div className="cd-status-badge">
                        <Check fill={colorPrimary()} />
                        <span>{contract?.getStatus()}</span>
                    </div>
                </div>

                {/* Supplier + Statement */}
                <div className="cd-middle-section">
                    <div className="cd-middle-left">
                        <p className="cd-section-label">{isNafV3() ? t('transporter') : t('agent')}</p>
                        <div className="cd-supplier-card">
                            <Avatar
                                style={{ width: "48px", height: "48px" }}
                                firstname={supplier?.firstname}
                                lastname={supplier?.lastname}
                                uri={supplier?.user?.avatar_url} />
                            <div className="cd-supplier-info">
                                <p className="cd-supplier-name">{supplier?.firstname} {supplier?.transactor?.person?.lastname}</p>
                                <p className="cd-supplier-location">{supplier?.location?.village}{supplier?.transactor?.person?.location?.district ? `, ${supplier.transactor.person.location.district}` : ''}</p>
                            </div>
                        </div>
                    </div>
                    <div className="cd-middle-right">
                        <StatementComponent statement={contract?.getBuyerSaleStatement()} />
                    </div>
                </div>

                {/* Map + Photos */}
                <div className="map-photo-container">
                    {
                        shouldDisplayHarvest() || shouldDisplayReceipt() ?
                            <div className="photo-container">
                                {shouldDisplayHarvest() ? generateHarvestPicture() : <></>}
                                {shouldDisplayReceipt() ? displayReceipt(contract) : <></>}
                            </div>
                            : <></>
                    }
                    <div className="map-container-contract">
                        <div className="map-container-contract-ar">
                            <div style={{ position: "relative", width: "100%", height: "100%" }}>
                                <MapBox lat={center ? center[1] : defaultLat} lng={center ? center[0] : defaultLng} onLoaded={(map) => { onMapLoaded(map) }} onWebGlDisable={webGlNotActivated} />
                                {
                                    !hasPositionOnMap() &&
                                    <div className="cd-map-empty-overlay">
                                        <EmptyView type="check" isVisible={true} title={t('no_harvest_for_contract')} />
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>

            </div>
            <Loader isLoading={isLoading} />
        </div>
    )
}

export default ContractDetail